import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface BrandingFooterProps {
  isPlaying: boolean;
  prevStep: () => void;
  quota: any;
  canSubmit?: boolean;
  isSubmitting?: boolean;
}

export const BrandingFooter = ({
  isPlaying,
  prevStep,
  quota,
  canSubmit,
  isSubmitting,
}: BrandingFooterProps) => {
  const { t } = useTranslation();

  return (
    <div className="hidden lg:flex justify-between items-center pt-8 border-t border-black/5">
      <Button
        type="button"
        variant="outline"
        disabled={isPlaying}
        onClick={prevStep}
        className="rounded-full border-black/10 hover:bg-white px-12 py-7 2k:px-20 2k:py-12 4k:px-30 4k:py-18 text-lg 2k:text-3xl 4k:text-5xl cursor-pointer"
      >
        {t("common.back") || "Back"}
      </Button>

      <div className="flex items-center gap-8">
        {quota && (
          <p
            className={cn(
              "hidden md:flex text-sm font-bold tracking-tight",
              "2k:text-2xl",
              "4k:text-4xl",
              quota.videosRemaining > 0
                ? "text-muted-foreground"
                : "text-destructive",
            )}
          >
            {quota.videosRemaining > 0
              ? t("createVideo.videosRemaining", {
                  count: quota.videosRemaining,
                  label:
                    quota.videosRemaining === 1
                      ? t("createVideo.videoSingular")
                      : t("createVideo.videoPlural"),
                })
              : t("createVideo.quotaExhausted")}
          </p>
        )}

        <Button
          type="submit"
          disabled={!canSubmit || quota?.videosRemaining === 0 || isPlaying || isSubmitting}
          className={cn(
            "rounded-full bg-black hover:bg-black/90 text-white px-12 py-7 2k:px-20 2k:py-12 4k:px-30 4k:py-18 text-lg 2k:text-3xl 4k:text-5xl cursor-pointer shadow-2xl shadow-black/20",
            "disabled:opacity-30",
          )}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-6 h-6 mr-2 animate-spin" />
              {t("createVideo.creating")}
            </>
          ) : (
            t("createVideo.createButton")
          )}
        </Button>
      </div>
    </div>
  );
};
